import { useState, useEffect, useCallback } from 'react';

class ViewedProductsManager {
    constructor() {
        this.storageKey = 'viewed_products';
        this.maxItems = 50; // Максимальное количество просмотренных товаров
        this.init();
    }

    init() {
        this.loadViewedProducts();
    }

    loadViewedProducts() {
        try {
            const stored = localStorage.getItem(this.storageKey);
            if (stored) {
                this.viewedProducts = JSON.parse(stored);
            } else {
                this.viewedProducts = [];
            }
        } catch (error) {
            console.error('Error loading viewed products:', error);
            this.viewedProducts = [];
        }
    }

    saveViewedProducts() {
        try {
            localStorage.setItem(this.storageKey, JSON.stringify(this.viewedProducts));
        } catch (error) {
            console.error('Error saving viewed products:', error);
        }
    }

    addViewedProduct(spuId) {
        if (!spuId || spuId === 'undefined') {
            //console.log('❌ Invalid spuId for viewed product:', spuId);
            return false;
        }

        const now = new Date().toISOString();
        
        // Удаляем существующий товар, если он уже есть
        this.viewedProducts = this.viewedProducts.filter(item => item.spuId !== spuId);
        
        // Добавляем новый товар в начало списка
        this.viewedProducts.unshift({
            spuId,
            viewedAt: now
        });

        // Ограничиваем количество товаров
        if (this.viewedProducts.length > this.maxItems) {
            this.viewedProducts = this.viewedProducts.slice(0, this.maxItems);
        }

        this.saveViewedProducts();
        //console.log('✅ Added viewed product:', spuId);
        return true;
    }

    removeViewedProduct(spuId) {
        this.viewedProducts = this.viewedProducts.filter(item => item.spuId !== spuId);
        this.saveViewedProducts();
        //console.log('🗑️ Removed viewed product:', spuId);
        return true;
    }

    clearViewedProducts() {
        this.viewedProducts = [];
        this.saveViewedProducts();
        //console.log('🧹 Cleared all viewed products');
        return true;
    }

    getViewedProducts() {
        return [...this.viewedProducts];
    }

    getViewedProductsCount() {
        return this.viewedProducts.length;
    }

    isProductViewed(spuId) {
        return this.viewedProducts.some(item => item.spuId === spuId);
    }
}

let viewedProductsManager = null;

const getViewedProductsManager = () => {
    if (!viewedProductsManager) {
        viewedProductsManager = new ViewedProductsManager();
    }
    return viewedProductsManager;
};

const useViewedProducts = () => {
    const [viewedProducts, setViewedProducts] = useState([]);
    const [viewedProductsCount, setViewedProductsCount] = useState(0);

    const updateViewedProductsState = useCallback(() => {
        const manager = getViewedProductsManager();
        setViewedProducts(manager.getViewedProducts());
        setViewedProductsCount(manager.getViewedProductsCount());
    }, []);

    useEffect(() => {
        updateViewedProductsState();
    }, [updateViewedProductsState]);

    const addViewedProduct = useCallback((spuId) => {
        //console.log('👁️ useViewedProducts.addViewedProduct() called with:', spuId);
        const manager = getViewedProductsManager();
        const success = manager.addViewedProduct(spuId);
        if (success) {
            updateViewedProductsState();
        }
        return success;
    }, [updateViewedProductsState]);

    const removeViewedProduct = useCallback((spuId) => {
        //console.log('🗑️ useViewedProducts.removeViewedProduct() called with:', spuId);
        const manager = getViewedProductsManager();
        const success = manager.removeViewedProduct(spuId);
        if (success) {
            updateViewedProductsState();
        }
        return success;
    }, [updateViewedProductsState]);

    const clearViewedProducts = useCallback(() => {
        //console.log('🧹 useViewedProducts.clearViewedProducts() called');
        const manager = getViewedProductsManager();
        const success = manager.clearViewedProducts();
        if (success) {
            updateViewedProductsState();
        }
        return success;
    }, [updateViewedProductsState]);

    const isProductViewed = useCallback((spuId) => {
        const manager = getViewedProductsManager();
        return manager.isProductViewed(spuId);
    }, []);

    return {
        viewedProducts,
        viewedProductsCount,
        addViewedProduct,
        removeViewedProduct,
        clearViewedProducts,
        isProductViewed,
        updateViewedProductsState
    };
};

export default useViewedProducts;
